import { createContext, useCallback, useEffect, useState } from "react";

import useMediaQuery from "hooks/useMediaQuery";

interface Props {
  index?: number;
  onIndexChange?: (_any?: any) => void;
  maxIndex?: number;
  children?: any;
}

interface ContextProps {
  onTouchStart: (_e?: any) => void;
  onTouchEnd: (_e?: any) => void;
  onTouchMove: (_e?: any) => void;
  enableSwipe: boolean;
  setEnabledSwipe?: (_e: any) => void;
}

export const SwipperContext = createContext<ContextProps>({
  onTouchStart: () => {},
  onTouchEnd: () => {},
  onTouchMove: () => {},
  enableSwipe: true,
  setEnabledSwipe: () => {},
});

export default function useSwipper({
  children,
  index = 0,
  maxIndex = 1,
  onIndexChange,
}: Props) {
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [swipeIndex, setSwipeIndex] = useState(index);
  const [enabled, setEnabled] = useState(true);
  const { mobile } = useMediaQuery();

  const minSwipeDistance = 50;

  const onTouchStart = useCallback((e: any) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  }, []);

  const onTouchMove = (e: any) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  useEffect(() => {
    setSwipeIndex(index);
  }, [index]);

  const onTouchEnd = useCallback(() => {
    if (!enabled) {
      setTimeout(() => {
        setEnabled(true);
      }, 500);
      return;
    }

    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    if (isLeftSwipe || isRightSwipe) {
      if (isRightSwipe) {
        if (swipeIndex - 1 >= 0) {
          setSwipeIndex(swipeIndex - 1);
          onIndexChange?.(swipeIndex - 1);
        }
      } else {
        if (swipeIndex + 1 < maxIndex) {
          setSwipeIndex(swipeIndex + 1);
          onIndexChange?.(swipeIndex + 1);
        }
      }
    }
  }, [touchStart, touchEnd, swipeIndex, onIndexChange, maxIndex, enabled]);

  useEffect(() => {
    if (!mobile) return;

    const updateScrolling = () => {
      setEnabled(false);
    };

    window.addEventListener("scroll", updateScrolling, { passive: true });

    return () => {
      window.removeEventListener("scroll", updateScrolling);
    };
  }, [mobile]);

  return (
    <SwipperContext.Provider
      value={{
        onTouchStart,
        onTouchEnd,
        onTouchMove,
        enableSwipe: enabled,
        setEnabledSwipe: setEnabled,
      }}
    >
      <div
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        onTouchMove={onTouchMove}
      >
        {children}
      </div>
    </SwipperContext.Provider>
  );
}
